import { useState } from 'react';
import * as React from 'react';

import CreateIcon from '@mui/icons-material/Create';
import {
  Button,
  DialogContent,
  Fab,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import { useTheme } from '@mui/material/styles';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';
import type { UserUpdateDTO } from '@klangio/ai2notes-ts-client';
import type { WithUser } from '../../../context';
import { withUserContext } from '../../../context';
import type { ITheme } from '../../../theme';
import { UserAvatar } from '../../atoms';
import SubscriptionBox from '../../molecules/SubscriptionBox/SubscriptionBox';
import CancelSubscriptionDialog from './CancelSubscriptionDialog';
import { DeleteUserAccountDialog } from './DeleteUserAccountDialog';

const useStyles = makeStyles()(() => ({
  avatar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  editButtons: {
    display: 'flex',
    gap: '1em',
    textAlign: 'left',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1em',
    minWidth: '20em',
    paddingTop: '6px',
  },
  row: {
    margin: '.4em',
    display: 'flex',
    justifyContent: 'space-between',
  },
  rowClose: {
    margin: '.5em',
    display: 'flex',
    justifyContent: 'end',
  },
  fab: {
    height: '37px',
  },
  title: {
    fontSize: '18px',
    margin: '.5em',
  },
}));

export interface UserDialogProperties extends WithUser {
  open: boolean;
  handleClose?: () => void;
  onSave?: (data: UserUpdateDTO) => void;
}

const UserDialog: React.FC<UserDialogProperties> = ({ user, open, handleClose, onSave }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme: ITheme = useTheme();
  const [hasChanged, setHasChanged] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState<string>('');
  const [showDeleteUserAccountDialog, setShowDeleteUserAccountDialog] = useState(false);
  const matchesSmallScreen = useMediaQuery(theme.breakpoints.down('xs'));

  const [userData, setUserData] = useState<UserUpdateDTO>({
    firstName: user.firstName,
    lastName: user.lastName,
  });

  const validationSchema = Yup.object({
    email: Yup.string().min(3, 'Min. 3 character').required('This field is required'),
    firstName: Yup.string().min(3, 'Min. 3 character').required('This field is required'),
    lastName: Yup.string().min(3, 'Min. 3 character').required('This field is required'),
  });

  const handleSave = () => {
    onSave(userData);
    setHasChanged(false);
    handleClose();
  };

  const formik = useFormik({
    initialValues: {
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
    },
    validationSchema,
    onSubmit: handleSave,
  });

  const setNewUserData = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    formik.handleChange(event);
    if (event.target.id === 'first_name_text_field') {
      setUserData({
        ...userData,
        firstName: event.target.value,
      });
    }
    if (event.target.id === 'last_name_text_field') {
      setUserData({
        ...userData,
        lastName: event.target.value,
      });
    }

    setHasChanged(true);
  };

  const setCancelSubscription = (subscrId: string) => {
    setShowCancelDialog(true);
    setSubscriptionId(subscrId);
  };

  const handleDeleteAccount = () => {
    setShowDeleteUserAccountDialog(true);
  };

  const handleCloseCancelDialog = () => {
    setShowCancelDialog(false);
  };
  return (
    <>
      <Dialog
        open={open && !showCancelDialog}
        onClose={handleClose}
        aria-labelledby="user-dialog"
        scroll="paper"
        maxWidth="xs"
        fullScreen={matchesSmallScreen}>
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle id="user-dialog">
            <div className={classes.avatar}>
              {t('dialog.user_info.title')}
              <UserAvatar />
            </div>
          </DialogTitle>
          <DialogContent>
            <div className={classes.content}>
              <TextField
                size="small"
                fullWidth
                id="email_text_field"
                name="email"
                value={formik.values.email}
                label={t('dialog.user_info.email')}
                className="gl-block"
                error={Boolean(formik.errors.email)}
                helperText={formik.errors.email}
                variant="outlined"
                disabled
              />
              <TextField
                size="small"
                fullWidth
                id="first_name_text_field"
                name="firstName"
                label={t('dialog.user_info.first_name')}
                variant="outlined"
                value={formik.values.firstName}
                error={Boolean(formik.errors.firstName)}
                helperText={formik.errors.firstName}
                onChange={setNewUserData}
                InputProps={{
                  readOnly: Boolean(!onSave),
                  startAdornment: (
                    <InputAdornment position="start">
                      <CreateIcon color="disabled" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                size="small"
                id="last_name_text_field"
                className="gl-block"
                name="lastName"
                label={t('dialog.user_info.last_name')}
                value={formik.values.lastName}
                error={Boolean(formik.errors.lastName)}
                helperText={formik.errors.lastName}
                variant="outlined"
                onChange={setNewUserData}
                InputProps={{
                  readOnly: Boolean(!onSave),
                  startAdornment: (
                    <InputAdornment position="start">
                      <CreateIcon color="disabled" />
                    </InputAdornment>
                  ),
                }}
              />
              <div className={classes.row}>
                <TextField
                  size="small"
                  id="ticket_text_field"
                  disabled
                  label={t('dialog.user_info.tickets')}
                  defaultValue={user.tickets}
                />
                <div>
                  {onSave && (
                    <Fab
                      className={classes.fab}
                      variant="extended"
                      color="primary"
                      disabled={!hasChanged}
                      type="submit">
                      {t('common.save')}
                    </Fab>
                  )}
                </div>
              </div>
            </div>
            <div className={classes.editButtons}>
              <Button color="primary" href="https://auth.klang.io/forgot">
                {t('dialog.user_info.change_password')}
              </Button>
              <Button id="delete-user-btn" color="error" onClick={handleDeleteAccount}>
                {t('dialog.user_info.delete_user')}
              </Button>
            </div>
            {user?.subscriptions.length === 1 && (
              <Typography className={classes.title}>
                {t('dialog.user_info.subscription.title')}
              </Typography>
            )}
            {user?.subscriptions.length > 1 && (
              <Typography className={classes.title}>
                {t('dialog.user_info.subscription.title_multi')}
              </Typography>
            )}
            {user?.subscriptions.map((subscr) => (
              <SubscriptionBox
                key={subscr.id}
                onCancelSubscription={(subscrId) => setCancelSubscription(subscrId)}
                subscription={subscr}
              />
            ))}
            {handleClose && (
              <div className={classes.rowClose}>
                <Fab
                  className={classes.fab}
                  variant="extended"
                  color="primary"
                  onClick={handleClose}>
                  {t('dialog.user_info.close')}
                </Fab>
              </div>
            )}
          </DialogContent>
        </form>
      </Dialog>
      {showCancelDialog && (
        <CancelSubscriptionDialog
          subscriptionId={subscriptionId}
          open={showCancelDialog}
          onClose={handleCloseCancelDialog}
        />
      )}
      <DeleteUserAccountDialog
        open={showDeleteUserAccountDialog}
        onClose={() => setShowDeleteUserAccountDialog(false)}
      />
    </>
  );
};

export default withUserContext(UserDialog);
